// Internationalization utilities: language detection, storage, and key resolution

const LANGUAGE_STORAGE_KEY = 'appLanguage';
const SUPPORTED_LANGUAGES = ['bg', 'en'];
const DEFAULT_LANGUAGE = 'bg';

export const getBrowserLanguage = () => {
  try {
    const nav = window.navigator;
    const lang = (nav.languages && nav.languages[0]) || nav.language || nav.userLanguage || DEFAULT_LANGUAGE;
    const normalized = String(lang).toLowerCase().slice(0, 2);
    return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : DEFAULT_LANGUAGE;
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
};

export const loadStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch (e) {
    // ignore storage errors
  }
  return null;
};

export const storeLanguage = (lang) => {
  try {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }
  } catch (e) {
    // ignore storage errors
  }
};

export const resolveKey = (translations, key) => {
  if (!key) return '';
  const parts = String(key).split('.');
  let node = translations;
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (node && Object.prototype.hasOwnProperty.call(node, part)) {
      node = node[part];
    } else {
      return null;
    }
  }
  return typeof node === 'string' ? node : null;
};

export const interpolate = (template, params) => {
  if (!template || !params) return template || '';
  return template.replace(/\{(\w+)\}/g, (_, p1) => {
    const value = Object.prototype.hasOwnProperty.call(params, p1) ? params[p1] : undefined;
    return value !== null && value !== undefined ? String(value) : `{${p1}}`;
  });
};

export const getInitialLanguage = () => loadStoredLanguage() || getBrowserLanguage() || DEFAULT_LANGUAGE;

// Pluralization (basic one/other categories for bg/en)
export const getPluralCategory = (language, count) => {
  const n = Number(count);
  if (!Number.isFinite(n)) return 'other';
  switch (language) {
    case 'bg':
      return n === 1 ? 'one' : 'other';
    case 'en':
      return n === 1 ? 'one' : 'other';
    default:
      return n === 1 ? 'one' : 'other';
  }
};

const isDev = () => {
  try {
    return process && process.env && process.env.NODE_ENV !== 'production';
  } catch (e) {
    return false;
  }
};

export const validateKeyExists = (translations, key) => resolveKey(translations, key) !== null;

export const validateTemplateParams = (template, params) => {
  if (typeof template !== 'string') return true;
  const required = Array.from(template.matchAll(/\{(\w+)\}/g)).map((m) => m[1]);
  if (required.length === 0) return true;
  return required.every((k) => params && Object.prototype.hasOwnProperty.call(params, k));
};

export const translate = (translations, key, params = {}, options = {}) => {
  const { language = DEFAULT_LANGUAGE, count, defaultValue } = options;
  const raw = resolveKey(translations, key);
  if (raw === null || raw === undefined) {
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Missing translation key: ${key} (lang: ${language})`);
    }
    return defaultValue !== null && defaultValue !== undefined ? String(defaultValue) : String(key);
  }

  if (typeof raw === 'string') {
    return interpolate(raw, params);
  }

  // Object form: { one: '...', other: '...' }
  if (typeof raw === 'object' && raw) {
    const category = getPluralCategory(language, count);
    const chosen = raw[category] || raw.other || raw.one;
    const augmented = { ...params, count };
    return interpolate(String(chosen), augmented);
  }

  return String(raw);
};

export const i18nUtils = {
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  getBrowserLanguage,
  loadStoredLanguage,
  storeLanguage,
  resolveKey,
  interpolate,
  translate,
  getPluralCategory,
  validateKeyExists,
  validateTemplateParams,
  getInitialLanguage,
};

export default i18nUtils;


